import { Link } from 'react-router-dom'
import {
  Database, FileText, Inbox, Monitor, RefreshCw, Shield, Users,
} from 'lucide-react'
import { config } from '@/config/runtime-config'
import { KpiStrip, MetricCard, Panel, SectionHeader } from '@/components/erp/primitives'
import { useSession } from '@/shared/auth/session'
import { fmtDateTime, fmtNumber, humanise } from '@/shared/lib/format'
import { useLoad } from './hooks'
import { usersSummary, EMPTY_USER_FILTERS } from '@/features/users/api'
import { auditSummary } from '@/features/audit/api'
import { EMPTY_AUDIT_FILTERS } from '@/features/audit/api'
import { notificationSummary } from '@/features/notifications/api'
import { EMPTY_NOTIFICATION_FILTERS } from '@/features/notifications/api'
import { ChangePasswordCard } from './ChangePasswordCard'

/**
 * Administration hub.
 *
 * The counts come from the real summary endpoints rather than
 * `totalElements` on a size-1 page — that trick worked before the summaries
 * existed and it cost a full COUNT per card.
 *
 * Each card is a real link now that audit, notifications, users and roles are
 * their own routes, so the browser's own affordances (open in a new tab,
 * middle-click, back) work.
 */
const CARDS = [
  { to: '/users', label: 'User Management', icon: Users, desc: 'Accounts, scope, roles and sessions' },
  { to: '/roles', label: 'Roles & Permissions', icon: Shield, desc: 'Create roles and set their grants' },
  { to: '/audit', label: 'System Audit Log', icon: FileText, desc: 'Append-only activity trail' },
  { to: '/notifications', label: 'Notification Log', icon: Inbox, desc: 'Outbound delivery and failures' },
  { to: '/users?view=sessions', label: 'Sessions', icon: Monitor, desc: 'Who is signed in, and from where' },
  { to: '/administration?tab=settings', label: 'System Settings', icon: Database, desc: 'Runtime configuration' },
]

export function OverviewTab() {
  const user = useSession((s) => s.user)
  const authorities = useSession((s) => s.authorities)

  // Each summary is permission-gated, so a caller without the authority gets a
  // 403 — caught to null rather than blanking the whole page.
  const users = useLoad(() => usersSummary(EMPTY_USER_FILTERS).catch(() => null), [])
  const audit = useLoad(() => auditSummary(EMPTY_AUDIT_FILTERS).catch(() => null), [])
  const notif = useLoad(
    () => notificationSummary(EMPTY_NOTIFICATION_FILTERS).catch(() => null), [],
  )

  const reloadAll = () => { users.reload(); audit.reload(); notif.reload() }

  const u = users.data
  const a = audit.data
  const n = notif.data

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Administration"
        description="Identity, permissions, configuration and the audit trail for this deployment."
        right={
          <button className="erp-btn" onClick={reloadAll}>
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        }
      />

      <KpiStrip>
        <MetricCard
          label="Users"
          value={u ? fmtNumber(u.total) : '—'}
          sub={u ? `${fmtNumber(u.byStatus.ACTIVE ?? 0)} active · ${fmtNumber(u.pendingInvitations)} invited` : undefined}
          accent
        />
        <MetricCard
          label="Audit events today"
          value={a ? fmtNumber(a.today) : '—'}
          sub={a ? `${fmtNumber(a.failures)} failures all time` : undefined}
        />
        <MetricCard
          label="Messages sent"
          value={n ? fmtNumber(n.sent) : '—'}
          sub={n ? `${fmtNumber(n.failed)} failed · ${fmtNumber(n.pending)} pending` : undefined}
          deltaType={n && n.failed > 0 ? 'down' : 'neutral'}
        />
        <MetricCard
          label="Your permissions"
          value={fmtNumber(authorities.length)}
          sub="resolved by the server"
        />
        <MetricCard
          label="Environment"
          value={config.environment.toUpperCase()}
          sub={config.release}
        />
      </KpiStrip>

      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
        {CARDS.map((c) => {
          const Icon = c.icon
          return (
            <Link
              key={c.to}
              to={c.to}
              className="erp-card px-4 py-3 text-left hover:bg-accent/40 transition-colors flex items-start gap-3"
            >
              <Icon className="h-4 w-4 text-chocolate mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-erp-md font-semibold truncate">{c.label}</div>
                <div className="text-erp-xs text-muted-foreground">{c.desc}</div>
              </div>
            </Link>
          )
        })}
      </div>

      <div className="grid gap-4 lg:grid-cols-2 items-start">
        <Panel
          title="Signed in as"
          description="Identity resolved from the backend on every session restore."
        >
          <dl className="grid gap-y-2 gap-x-6 text-erp-sm" style={{ gridTemplateColumns: 'minmax(120px,auto) 1fr' }}>
            <dt className="text-muted-foreground">Name</dt><dd>{user?.name ?? '—'}</dd>
            <dt className="text-muted-foreground">Email</dt><dd className="font-mono break-all">{user?.email ?? '—'}</dd>
            <dt className="text-muted-foreground">Scope</dt><dd>{humanise(user?.scopeLevel)}</dd>
            <dt className="text-muted-foreground">Clearance</dt><dd className="font-mono">{user?.clearance ?? '—'}</dd>
            <dt className="text-muted-foreground">Roles</dt>
            <dd>
              {user?.roles?.length
                ? <div className="flex flex-wrap gap-1">
                    {user.roles.map((r) => (
                      <span
                        key={r.id}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-erp-xs"
                        style={{ borderColor: (r.colour ?? '') || 'var(--color-border)' }}
                      >
                        <span className="h-1.5 w-1.5 rounded-full"
                              style={{ background: r.colour ?? 'var(--color-muted-foreground)' }} />
                        {r.displayName}
                      </span>
                    ))}
                  </div>
                : <span className="text-muted-foreground">none</span>}
            </dd>
            <dt className="text-muted-foreground">Last sign-in</dt>
            <dd className="erp-metric">{fmtDateTime(user?.lastLoginAt)}</dd>
            <dt className="text-muted-foreground">Bootstrap SU</dt>
            <dd>{user?.bootstrap ? 'Yes' : 'No'}</dd>
          </dl>
        </Panel>

        <ChangePasswordCard />
      </div>
    </div>
  )
}
